import React, { useEffect, useState, useMemo } from 'react';
import API_BASE_URL from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useContent } from '../hooks/useContent';
import { Capacitor } from '@capacitor/core';
import '../styles/components/profileTabs.css';
import '../styles/components/admin.css';

interface AdminUser {
  id: number;
  email: string;
  display_name: string;
  role?: 'admin' | 'user';
  created_at?: string;
  is_guest?: boolean;
  is_archived?: boolean;
  last_seen_at?: string;
}

const UsersTab: React.FC = () => {
  const { token } = useAuth();
  const { profile } = useContent();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(true);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const allUsersRef = React.useRef<HTMLDivElement>(null);
  const scrollLockIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  const authHeader = useMemo(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const fetchUsers = async (search = '') => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: '1',
        page_size: '1000',
        sort: 'last_seen_at',
        dir: 'desc',
        include_archived: 'true',
      });
      if (search) {
        params.set('search', search);
      }
      const resp = await fetch(`${API_BASE_URL}/api/auth/admin/users?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `Failed to fetch users: ${resp.status}`);
      }
      const data = await resp.json();
      const sorted = (data.users || []).sort((a: AdminUser, b: AdminUser) => b.id - a.id);
      setUsers(sorted);
    } catch (e: any) {
      setError(e.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const archiveUser = async (userId: number) => {
    if (!token || actionLoading) return;
    
    const user = users.find(u => u.id === userId);
    if (!user) return;

    const confirmed = window.confirm(
      `Archive user "${user.display_name}" (${user.email})?\n\n` +
      'This will soft-delete the user and their items. The user can be unarchived later.'
    );
    
    if (!confirmed) return;
    
    setActionLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/admin/users/${userId}/archive`, {
        method: 'POST',
        headers: authHeader,
      });
      
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to archive user');
      }
      
      await fetchUsers(searchQuery);
      setSelectedUserId(null);
    } catch (e: any) {
      alert(`Error: ${e.message || 'Failed to archive user'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const unarchiveUser = async (userId: number) => {
    if (!token || actionLoading) return;
    
    const user = users.find(u => u.id === userId);
    if (!user) return;

    const confirmed = window.confirm(
      `Unarchive user "${user.display_name}" (${user.email})?\n\n` +
      'This will restore the user and their items.'
    );
    
    if (!confirmed) return;
    
    setActionLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/admin/users/${userId}/unarchive`, {
        method: 'POST',
        headers: authHeader,
      });
      
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to unarchive user');
      }
      
      await fetchUsers(searchQuery);
      setSelectedUserId(null);
    } catch (e: any) {
      alert(`Error: ${e.message || 'Failed to unarchive user'}`);
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [token]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Track tab visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Keyboard height tracking for iOS
  useEffect(() => {
    const isIOSNative = Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform();
    if (!isIOSNative) return;

    const { Keyboard } = require('@capacitor/keyboard');

    const showListener = Keyboard.addListener('keyboardWillShow', (info: any) => {
      setKeyboardHeight(info.keyboardHeight || 0);
    });

    const hideListener = Keyboard.addListener('keyboardWillHide', () => {
      setKeyboardHeight(0);
    });

    return () => {
      showListener.then((l: any) => l.remove());
      hideListener.then((l: any) => l.remove());
    };
  }, []);

  // Auto-refresh every 1 minute to keep online status current (only when tab is visible)
  useEffect(() => {
    if (!isTabVisible) return;

    const interval = setInterval(() => {
      if (!document.hidden) {
        fetchUsers(searchQuery);
      }
    }, 60000); // 1 minute

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, isTabVisible]);

  // Note: Removed scroll on typing useEffect to prevent up/down movement
  // All Users section scrolls to top once on focus and stays there

  // Calculate summary statistics
  const stats = useMemo(() => {
    const activeUsers = users.filter(u => !u.is_archived);
    const archivedUsers = users.filter(u => u.is_archived);
    
    // Online users: last seen within 15 minutes
    const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
    const onlineUsers = activeUsers.filter(u => 
      u.last_seen_at && new Date(u.last_seen_at) > fifteenMinutesAgo
    );

    return {
      total: users.length,
      active: activeUsers.length,
      archived: archivedUsers.length,
      online: onlineUsers.length,
    };
  }, [users]);

  // Get online users sorted by last seen
  const onlineUsers = useMemo(() => {
    const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
    return users
      .filter(u => !u.is_archived && u.last_seen_at && new Date(u.last_seen_at) > fifteenMinutesAgo)
      .sort((a, b) => {
        if (!a.last_seen_at) return 1;
        if (!b.last_seen_at) return -1;
        return new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime();
      });
  }, [users]);

  const formatLastSeen = (lastSeenAt?: string) => {
    if (!lastSeenAt) return 'Never';
    
    const now = Date.now();
    const lastSeen = new Date(lastSeenAt).getTime();
    const diffMs = now - lastSeen;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return new Date(lastSeenAt).toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Calculate dynamic padding: add buffer for short lists, just keyboard height for long lists
  const bottomPadding = keyboardHeight > 0 
    ? `${keyboardHeight + (users.length <= 5 ? 600 : 0)}px` 
    : '0';

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: '12px',
      paddingBottom: bottomPadding
    }}>
      {/* Summary Statistics */}
      <div style={{ 
        background: 'var(--color-bg-primary)',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        margin: 0
      }}>
        <div className="profile-section">
          <h3>User Summary</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
            gap: '12px',
            marginTop: '12px'
          }}>
            <div style={{ 
              padding: '16px', 
              background: 'var(--color-bg-secondary)', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--color-text-primary)' }}>
                {stats.total}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                Total Users
              </div>
            </div>
            <div style={{ 
              padding: '16px', 
              background: '#ecfdf5', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#059669' }}>
                {stats.active}
              </div>
              <div style={{ fontSize: '13px', color: '#047857', marginTop: '4px' }}>
                Active
              </div>
            </div>
            <div style={{ 
              padding: '16px', 
              background: '#fef3c7', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#d97706' }}>
                {stats.online}
              </div>
              <div style={{ fontSize: '13px', color: '#b45309', marginTop: '4px' }}>
                Online Now
              </div>
            </div>
            <div style={{ 
              padding: '16px', 
              background: 'var(--color-danger-light)', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--color-danger)' }}>
                {stats.archived}
              </div>
              <div style={{ fontSize: '13px', color: '#b91c1c', marginTop: '4px' }}>
                Archived
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Online Users */}
      {onlineUsers.length > 0 && (
        <div style={{ 
          background: 'var(--color-bg-primary)',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          margin: 0
        }}>
          <div className="profile-section">
            <h3>Online Users ({onlineUsers.length})</h3>
            <div style={{ marginTop: '12px' }}>
              {onlineUsers.map(user => (
                <div 
                  key={user.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    borderBottom: '1px solid var(--color-bg-tertiary)',
                    cursor: 'pointer',
                    background: selectedUserId === user.id ? 'var(--color-bg-secondary)' : 'transparent',
                    transition: 'background 0.15s'
                  }}
                  onClick={() => setSelectedUserId(selectedUserId === user.id ? null : user.id)}
                  onMouseEnter={(e) => {
                    if (selectedUserId !== user.id) {
                      e.currentTarget.style.background = 'var(--color-bg-hover)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedUserId !== user.id) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: 'var(--color-success)',
                      flexShrink: 0
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '500', fontSize: '14px', color: 'var(--color-text-primary)' }}>
                        {user.display_name}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {user.email}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-faint)', flexShrink: 0 }}>
                    {formatLastSeen(user.last_seen_at)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* All Users List */}
      <div ref={allUsersRef} className="profile-card no-padding">
        <div className="profile-section">
          <h3>All Users</h3>
          
          {/* Search Box - Sticky within All Users section */}
          <div style={{ 
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: 'var(--color-bg-primary)',
            padding: '0 0 12px 0',
            marginTop: '12px'
          }}>
            <div style={{ position: 'relative' }}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search by name, email, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => {
                  setIsSearchFocused(true);
                  // Scroll All Users to top immediately to prevent iOS from scrolling down first
                  if (allUsersRef.current) {
                    allUsersRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
                  }
                  // Start scroll lock interval to maintain position while typing
                  scrollLockIntervalRef.current = setInterval(() => {
                    if (allUsersRef.current) {
                      const rect = allUsersRef.current.getBoundingClientRect();
                      // If All Users has scrolled down (top > 0), scroll it back to top
                      if (rect.top > 0) {
                        allUsersRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
                      }
                    }
                  }, 100);
                }}
                onBlur={() => {
                  setIsSearchFocused(false);
                  // Clear scroll lock interval
                  if (scrollLockIntervalRef.current) {
                    clearInterval(scrollLockIntervalRef.current);
                    scrollLockIntervalRef.current = null;
                  }
                }}
                style={{
                  width: '100%',
                  height: '40px',
                  padding: '0 40px 0 12px',
                  border: 'none',
                  borderRadius: '20px',
                  background: 'rgba(142, 142, 147, 0.12)',
                  color: 'var(--color-text-primary)',
                  fontSize: '15px',
                  fontWeight: '400',
                  outline: 'none',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box'
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    border: 'none',
                    background: 'rgba(142, 142, 147, 0.3)',
                    color: 'var(--color-text-inverse)',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(142, 142, 147, 0.5)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(142, 142, 147, 0.3)'}
                >
                  ×
                </button>
              )}
            </div>
            {searchQuery && (
              <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--color-text-muted)' }}>
                Found {users.length} user{users.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          {loading && <p style={{ padding: '0 16px' }}>{profile.users_loading}</p>}
          {error && <p style={{ color: 'red', padding: '0 16px' }}>{error}</p>}
          {!loading && !error && (
            <div className="stats-table-scroll-container">
              <table className="stats-table-sticky">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th className="stats-table-sticky-col">Email</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Type</th>
                    <th>Last Seen</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td style={{ opacity: u.is_archived ? 0.5 : 1 }}>{u.id}</td>
                      <td
                        className={`stats-table-sticky-col${expandedUserId === u.id ? ' stats-table-sticky-col-expanded' : ''}`}
                        onClick={() => setExpandedUserId(expandedUserId === u.id ? null : u.id)}
                        title={u.email}
                        style={{ opacity: u.is_archived ? 0.5 : 1 }}
                      >{u.email || <span style={{ color: 'var(--color-text-faint)', fontStyle: 'italic' }}>—</span>}</td>
                      <td style={{ opacity: u.is_archived ? 0.5 : 1 }}>
                        {u.display_name}
                        {u.is_archived && <span style={{ marginLeft: 8, color: 'var(--color-danger)', fontSize: '12px', fontWeight: 600, opacity: 1 }}>ARCHIVED</span>}
                      </td>
                      <td style={{ opacity: u.is_archived ? 0.5 : 1 }}>{u.role || 'user'}</td>
                      <td style={{ opacity: u.is_archived ? 0.5 : 1 }}>
                        {u.is_guest ? (
                          <span style={{ display: 'inline-block', padding: '2px 8px', background: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)', borderRadius: '4px', fontSize: '12px', fontWeight: 600 }}>
                            Guest
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-text-faint)' }}>User</span>
                        )}
                      </td>
                      <td style={{ fontSize: '13px', opacity: u.is_archived ? 0.5 : 1 }}>{formatLastSeen(u.last_seen_at)}</td>
                      <td style={{ fontSize: '13px', opacity: u.is_archived ? 0.5 : 1 }}>
                        {u.created_at ? new Date(u.created_at).toLocaleDateString(undefined, { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        }) : ''}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {u.is_archived ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                unarchiveUser(u.id);
                              }}
                              disabled={actionLoading}
                              style={{
                                padding: '4px 12px',
                                fontSize: '12px',
                                background: 'var(--color-success)',
                                color: 'var(--color-text-inverse)',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: actionLoading ? 'not-allowed' : 'pointer',
                                opacity: actionLoading ? 0.6 : 1
                              }}
                            >
                              Unarchive
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                archiveUser(u.id);
                              }}
                              disabled={actionLoading}
                              style={{
                                padding: '4px 12px',
                                fontSize: '12px',
                                background: 'var(--color-danger)',
                                color: 'var(--color-text-inverse)',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: actionLoading ? 'not-allowed' : 'pointer',
                                opacity: actionLoading ? 0.6 : 1
                              }}
                            >
                              Archive
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersTab;
